export type ArticleCategory = 'PR' | 'Markets' | 'Opinion' | 'Earnings' | 'M&A' | 'News' | 'Article';

/**
 * Derives a display category from an article URL.
 * Specific sub-segment checks (/m-a/, /earnings/) run before first-segment mapping,
 * so earnings/M&A articles nested under /news/ are still caught. Unknown paths → 'Article'.
 * Handles both relative ("/pressreleases/...") and absolute ("https://www.benzinga.com/...") URLs.
 */
export const getArticleCategory = (articleUrl: string | undefined): ArticleCategory => {
  if (!articleUrl) return 'Article';

  let path = articleUrl;
  try {
    path = articleUrl.startsWith('http') ? new URL(articleUrl).pathname : articleUrl;
  } catch {
    path = articleUrl;
  }
  path = path.toLowerCase();

  if (path.includes('/m-a/') || path.includes('/m-and-a/') || path.includes('/mergers')) return 'M&A';
  if (path.includes('/earnings/')) return 'Earnings';

  const firstSegment = path.replace(/^\/+/, '').split('/')[0] ?? '';
  switch (firstSegment) {
    case 'pressreleases':
    case 'press-releases':
      return 'PR';
    case 'markets':
      return 'Markets';
    case 'opinion':
      return 'Opinion';
    case 'news':
      return 'News';
    default:
      return 'Article';
  }
};

/**
 * Categories distinct enough to surface as a chip in search results. The generic
 * buckets (`Article`, `News`, `Markets`, `Opinion`) are all "just an article" to a
 * reader, so they render without a chip — the badge only appears when it carries
 * real signal. `getArticleCategory` still returns those values; they're just not shown.
 */
export const CHIP_CATEGORIES: ReadonlySet<ArticleCategory> = new Set<ArticleCategory>(['Earnings', 'M&A', 'PR']);
